import { useSelector } from 'react-redux';
import { Typography } from '@components/Typography';
import TableContainer from '@mui/material/TableContainer';
import ActionIconButton from '@components/ActionIconButton';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import EditIcon from '@mui/icons-material/EditRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Table, getPaginationLabels, OwnerCell } from '@components/Table';
import { OWNER_TYPE } from '@components/OwnerIcon';
import { useTranslation, useHasAccessToAsset } from '@hooks';
import { formatAssignedEntities } from '@helpers/formatEntitiesToString';
import { REVIEW_TYPE } from '@constants/autoreply-rules';
import { RIGHTS } from '@constants/user';
import { FORM_TYPE } from '../EditModal';

const getTableHeader = t => {
    return [
        {
            key: 0,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('ReviewsAutoreplies.ruleName')}
                </Typography>
            ),
        },
        {
            key: 1,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.locations')}
                </Typography>
            ),
        },
        {
            key: 2,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.rating')}
                </Typography>
            ),
        },
        {
            key: 3,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('ReviewsAutoreplies.reviewType')}
                </Typography>
            ),
        },
        { key: 4, component: <Typography align="left" variant="subtitle2"></Typography> },
    ];
};

const getTableBody = (data, allCompanies, allBrands, onClickAction, t, canWriteRule) => {
    return data.map(rule => {
        const canWrite = canWriteRule(rule);
        const isBrandScoped = !rule.locations?.length && !!rule.brands?.length;
        const ownerNames = isBrandScoped
            ? formatAssignedEntities(allBrands, rule.brands, { idKey: 'id', labelKey: 'title', limit: 30 })
            : formatAssignedEntities(allCompanies, rule.locations, {
                  idKey: 'companyId',
                  labelKey: 'internalName',
                  limit: 30,
              });
        return [
            {
                component: (
                    <Typography variant="subtitle2" align="left">
                        {rule.name}
                    </Typography>
                ),
            },
            {
                component: (
                    <OwnerCell type={isBrandScoped ? OWNER_TYPE.BRAND : OWNER_TYPE.LOCATION} name={ownerNames} />
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left">
                        {`(${rule.ratings.length}) ${rule.ratings.join(', ')}`}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography
                        variant="body2"
                        align="left"
                        sx={{
                            textTransform: 'lowercase',
                            maxWidth: '180px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {`(${rule.reviewTypes.length}) ${rule.reviewTypes
                            .map(reviewType =>
                                reviewType === REVIEW_TYPE.WITHOUT_COMMENT
                                    ? t('ReviewsAutoreplies.withoutComment')
                                    : t('ReviewsAutoreplies.withComment'),
                            )
                            .join(', ')}`}
                    </Typography>
                ),
            },
            ...(canWrite
                ? [
                      {
                          component: (
                              <Typography align="right" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                  <ActionIconButton
                                      title={t('common.edit')}
                                      onClick={() => onClickAction(rule, FORM_TYPE.EDIT)}
                                  >
                                      <EditIcon viewBox="0 0 22 22" />
                                  </ActionIconButton>
                                  <ActionIconButton
                                      title={t('common.delete')}
                                      onClick={() => onClickAction(rule, FORM_TYPE.DELETE)}
                                  >
                                      <DeleteIcon viewBox="0 0 22 22" />
                                  </ActionIconButton>
                              </Typography>
                          ),
                      },
                  ]
                : [
                      {
                          component: (
                              <Typography align="right">
                                  <ActionIconButton
                                      title={t('common.view')}
                                      onClick={() => onClickAction(rule, FORM_TYPE.VIEW)}
                                  >
                                      <VisibilityIcon viewBox="0 0 22 22" />
                                  </ActionIconButton>
                              </Typography>
                          ),
                      },
                  ]),
        ];
    });
};

const AutoreplyRulesTable = ({
    setSelectedRule,
    setDeleteModalOpen,
    setModal,
    setPage,
    page,
    setRowsPerPage,
    rowsPerPage,
    companies,
    brands,
}) => {
    const { t } = useTranslation();
    const { hasAccessToAssetByIds } = useHasAccessToAsset();
    const { data, loading, totalCount, pages } = useSelector(state => state.autoreplyRules);

    const canWriteRule = rule => hasAccessToAssetByIds(rule, 'reviews')[RIGHTS.WRITE];

    const onClickAction = (rule, type) => {
        setSelectedRule(rule);
        if (type === FORM_TYPE.DELETE) {
            setDeleteModalOpen(true);
            return;
        }
        setModal({ open: true, type });
    };

    const onPageChange = (_, newPage) => setPage(newPage);

    const onRowsPerPageChange = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <TableContainer>
            <Table
                withHover
                columnNames={getTableHeader(t)}
                data={data}
                rows={getTableBody(data, companies, brands, onClickAction, t, canWriteRule)}
                onRowClick={rule => onClickAction(rule, canWriteRule(rule) ? FORM_TYPE.EDIT : FORM_TYPE.VIEW)}
                isDataLoading={loading}
                withPagination
                paginationProps={{
                    colSpan: 5,
                    count: totalCount,
                    rowsPerPage,
                    page,
                    onPageChange,
                    onRowsPerPageChange,
                    ...getPaginationLabels(t),
                    pages,
                }}
            />
        </TableContainer>
    );
};

export default AutoreplyRulesTable;
